import { useEffect, useMemo, useState } from 'react'
import Joyride, { STATUS, type CallBackProps, type Step } from 'react-joyride'
import { useAuthStore } from '@/stores/authStore'
import { useTutorial } from '@/hooks/useTutorial'

// Joyride needs a CSS selector at step-build time, so we read the viewport
// once and pick the appropriate target for step 6 (the profile entry point).
// 640px = Tailwind's `sm` breakpoint, the same threshold Header.tsx uses
// to hide the desktop profile pill in favor of the hamburger.
const useProfileTourTarget = () => {
  const [target, setTarget] = useState('[data-tour="header-profile"]')
  useEffect(() => {
    const compute = () =>
      setTarget(
        window.innerWidth < 640
          ? '[data-tour="mobile-menu-trigger"]'
          : '[data-tour="header-profile"]',
      )
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [])
  return target
}

/**
 * One-time spotlight tour of the portal's primary navigation. Mounted
 * once at the Layout root; auto-runs when `useTutorial().shouldRun` is
 * true. Calling markComplete on Finish OR Skip persists the flag.
 *
 * Step targets use stable `data-tour="..."` attributes (Header for desktop,
 * MobileNav for narrow viewports). Joyride picks the first matching node,
 * which is whichever copy is actually rendered for the current viewport.
 */
const TutorialTour = () => {
  const { shouldRun, markComplete } = useTutorial()
  const firstName = useAuthStore((s) => s.user?.firstName) ?? 'there'
  const profileTarget = useProfileTourTarget()

  const steps: Step[] = useMemo(
    () => [
      {
        target: 'body',
        placement: 'center',
        title: `Welcome, ${firstName}`,
        content:
          "Quick tour of the portal — about a minute. You'll see this once.",
        disableBeacon: true,
      },
      {
        target: '[data-tour="nav-dashboard"]',
        title: 'Home',
        content: "Your home base — see what's happening this week.",
        disableBeacon: true,
      },
      {
        target: '[data-tour="nav-calendar"]',
        title: 'Calendar',
        content: 'Find association events and RSVP.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="nav-directory"]',
        title: 'Directory',
        content: 'Look up neighbors and cottages.',
        disableBeacon: true,
      },
      {
        target: '[data-tour="nav-bulletin"]',
        title: 'Bulletin Board',
        content: 'Post and read community announcements.',
        disableBeacon: true,
      },
      {
        target: profileTarget,
        title: 'Your profile',
        content:
          "Update your info anytime here. You're all set — enjoy the lake.",
        disableBeacon: true,
      },
    ],
    [firstName, profileTarget],
  )

  const handle = (data: CallBackProps) => {
    const { status } = data
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      void markComplete()
    }
  }

  if (!shouldRun) return null

  return (
    <Joyride
      steps={steps}
      callback={handle}
      run
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      disableScrolling={false}
      scrollOffset={80}
      styles={{
        options: {
          primaryColor: '#1B3A52',
          textColor: '#2A2520',
          backgroundColor: '#FAF7F1',
          arrowColor: '#FAF7F1',
          zIndex: 60,
        },
      }}
      locale={{
        last: 'Finish',
        skip: 'Skip tour',
        next: 'Next',
        back: 'Back',
      }}
    />
  )
}

export default TutorialTour
